import { TranslationKey } from './../../../types/translation/TranslationKey';
import React from 'react';
import { ProductTransaction, ProductTransactionType } from '../../../types/productTransaction';
import { StockLocation } from '../../../types/stock_location/stockLocation';
import ProductTransactionStatusLabel from './ProductTransactionStatusLabel';
import { toMap } from '../../../util/map.util';
import { removeDiacritics, toFilterString } from '../../../util/string.util';
import { Bin } from '../../../types/bin/bin';
import { ProductMasterData } from '../../../types/product_master_data/productMasterData';
import { Lot } from '../../../types/lot/lot';
import { User } from '../../../types/user/user';
import { CustomFieldType } from '../../../types/custom_field/customField';
import { TableHeader } from '../../components/Table';
import { t } from '../../../types/translation/Translator';
import { Container } from '../../../types/container';
import { ContainerType } from '../../../types/container_type/containerType';
import Grid from '@mui/material/Grid';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { CustomFieldUtil } from '../../../types/custom_field/customField.util';
import { ProductOrContainerNameColumn } from '../../components/Table/ProductOrContainer';
import Icon, { IconType } from '../../icons/Icon';
import { UnitOfMeasure } from '../../../types/uom';
import { TableSettings } from '../../../types/table_settings/TableSettings';
import { OrderProductTransactionTableSettingsColumn } from '../../../types/table_settings/productTransaction/OrderProductTransactionTableSettings';
import { DateTimeUtil } from '../../../util/date.util';
import dayjs from 'dayjs';

export const productTransactionTableHeaders = (
  settings: TableSettings<any>,
  stockLocations: Map<string, StockLocation>,
  bins: Map<string, Bin>,
  productMasterData: Map<string, ProductMasterData>,
  lots: Map<string, Lot>,
  users: Map<string, User>,
  containers: Map<string, Container>,
  containerTypes: Map<string, ContainerType>,
  locale: string,
) => {
  const wantedColumns = toMap(settings.columns, 'column');

  const columns: TableHeader<ProductTransaction>[] = [];

  if (wantedColumns.has(OrderProductTransactionTableSettingsColumn.type)) {
    columns.push({
      key: OrderProductTransactionTableSettingsColumn.type,
      column: OrderProductTransactionTableSettingsColumn.type,
      name: wantedColumns.get(OrderProductTransactionTableSettingsColumn.type)?.label || '',
      text: (item: ProductTransaction) => (item.type == ProductTransactionType.inbound ? 'Inbound' : 'Outbound'),
    });
  }

  if (wantedColumns.has(OrderProductTransactionTableSettingsColumn.createdAt)) {
    columns.push({
      key: OrderProductTransactionTableSettingsColumn.createdAt,
      column: OrderProductTransactionTableSettingsColumn.createdAt,
      name: wantedColumns.get(OrderProductTransactionTableSettingsColumn.createdAt)?.label || '',
      text: (item: ProductTransaction) => `${new Date(item.createdAt).toLocaleDateString(locale)}`,
    });
  }

  if (wantedColumns.has(OrderProductTransactionTableSettingsColumn.updatedAt)) {
    columns.push({
      key: OrderProductTransactionTableSettingsColumn.updatedAt,
      column: OrderProductTransactionTableSettingsColumn.updatedAt,
      name: wantedColumns.get(OrderProductTransactionTableSettingsColumn.updatedAt)?.label || '',
      text: (item: ProductTransaction) => `${new Date(item.updatedAt).toLocaleDateString(locale)}`,
    });
  }

  if (wantedColumns.has(OrderProductTransactionTableSettingsColumn.sourceStockLocation)) {
    columns.push({
      key: OrderProductTransactionTableSettingsColumn.sourceStockLocation,
      column: OrderProductTransactionTableSettingsColumn.sourceStockLocation,
      name: wantedColumns.get(OrderProductTransactionTableSettingsColumn.sourceStockLocation)?.label || '',
      text: (item: ProductTransaction) => {
        if (item.type === ProductTransactionType.inbound || !item.product.fromStockLocationId) return '';
        return (
          stockLocations.get(item.product.fromStockLocationId)?.name ||
          t(TranslationKey.unknownStockLocation).singular.label
        );
      },
    });
  }

  if (wantedColumns.has(OrderProductTransactionTableSettingsColumn.destinationStockLocation)) {
    columns.push({
      key: OrderProductTransactionTableSettingsColumn.destinationStockLocation,
      column: OrderProductTransactionTableSettingsColumn.destinationStockLocation,
      name: wantedColumns.get(OrderProductTransactionTableSettingsColumn.destinationStockLocation)?.label || '',
      text: (item: ProductTransaction) => {
        if (item.type === ProductTransactionType.outbound || !item.product.toStockLocationId) return '';
        return (
          stockLocations.get(item.product.toStockLocationId)?.name ||
          t(TranslationKey.unknownStockLocation).singular.label
        );
      },
    });
  }

  if (wantedColumns.has(OrderProductTransactionTableSettingsColumn.sourceBin)) {
    columns.push({
      key: OrderProductTransactionTableSettingsColumn.sourceBin,
      column: OrderProductTransactionTableSettingsColumn.sourceBin,
      name: wantedColumns.get(OrderProductTransactionTableSettingsColumn.sourceBin)?.label || '',
      text: (item: ProductTransaction) => {
        if (item.type === ProductTransactionType.inbound || !item.product.fromBinId) return '';
        return bins.get(item.product.fromBinId)?.name || t(TranslationKey.unknownBin).singular.label;
      },
    });
  }

  if (wantedColumns.has(OrderProductTransactionTableSettingsColumn.destinationBin)) {
    columns.push({
      key: OrderProductTransactionTableSettingsColumn.destinationBin,
      column: OrderProductTransactionTableSettingsColumn.destinationBin,
      name: wantedColumns.get(OrderProductTransactionTableSettingsColumn.destinationBin)?.label || '',
      text: (item: ProductTransaction) => {
        if (item.type === ProductTransactionType.outbound || !item.product.toBinId) return '';
        return bins.get(item.product.toBinId)?.name || t(TranslationKey.unknownBin).singular.label;
      },
    });
  }

  if (wantedColumns.has(OrderProductTransactionTableSettingsColumn.destinationContainer)) {
    columns.push({
      key: OrderProductTransactionTableSettingsColumn.destinationContainer,
      column: OrderProductTransactionTableSettingsColumn.destinationContainer,
      name: wantedColumns.get(OrderProductTransactionTableSettingsColumn.destinationContainer)?.label || '',
      text: (item: ProductTransaction) => {
        if (item.type === ProductTransactionType.outbound || !item.product.toContainerId) return '';

        return (
          containers.get(item.product.toContainerId)?.identifier ||
          item.metadata?.toContainer?.identifier ||
          t(TranslationKey.unknownContainer).singular.label
        );
      },
    });
  }

  if (wantedColumns.has(OrderProductTransactionTableSettingsColumn.sourceContainer)) {
    columns.push({
      key: OrderProductTransactionTableSettingsColumn.sourceContainer,
      column: OrderProductTransactionTableSettingsColumn.sourceContainer,
      name: wantedColumns.get(OrderProductTransactionTableSettingsColumn.sourceContainer)?.label || '',
      text: (item: ProductTransaction) => {
        if (item.type === ProductTransactionType.inbound || !item.product.fromContainerId) return '';

        return (
          containers.get(item.product.fromContainerId)?.identifier ||
          item.metadata?.fromContainer?.identifier ||
          t(TranslationKey.unknownContainer).singular.label
        );
      },
    });
  }

  if (wantedColumns.has(OrderProductTransactionTableSettingsColumn.productName)) {
    columns.push({
      key: OrderProductTransactionTableSettingsColumn.productName,
      column: OrderProductTransactionTableSettingsColumn.productName,
      name: wantedColumns.get(OrderProductTransactionTableSettingsColumn.productName)?.label || '',
      weight: 1.25,
      sortValue: item => {
        if (item.product.pmdId) {
          return (
            productMasterData.get(item.product.pmdId || '')?.productName ??
            t(TranslationKey.unknownProductReference).singular.label
          );
        } else if (item.product.containerId) {
          const container = containers.get(item.product.containerId);
          if (!container) return t(TranslationKey.unknownContainer).singular.label;

          return '!' + container.identifier;
        }
      },
      text: (item: ProductTransaction) => {
        if (item.product.containerId) {
          return (
            <ProductOrContainerNameColumn
              productMasterDataId={item.product.pmdId}
              containerId={item.product.containerId}
              productMasterData={productMasterData}
              containers={containers}
            />
          );
        } else {
          const pmd = productMasterData.get(item.product.pmdId || '');
          if (!pmd) return <></>;

          const name = pmd.productName || t(TranslationKey.unknownProductReference).singular.label;

          let icon = <Icon type={IconType.package} size={'size-5'} stroke='stroke-black' strokeWidth='stroke-1' />;
          if (pmd.kitManaged) {
            icon = <Icon type={IconType.briefCase1} size={'size-5'} stroke='stroke-black' strokeWidth='stroke-1' />;
          }

          return (
            <div className='flex flex-nowrap items-center overflow-hidden'>
              <div className={`pr-2 ${item.kitTransactionId ? 'pl-6' : ''}`}>{icon}</div>

              <span className='overflow-hidden text-ellipsis text-nowrap' title={name}>
                {name}
              </span>
            </div>
          );
        }
      },
    });
  }

  if (wantedColumns.has(OrderProductTransactionTableSettingsColumn.productNumber)) {
    columns.push({
      key: OrderProductTransactionTableSettingsColumn.productNumber,
      column: OrderProductTransactionTableSettingsColumn.productNumber,
      name: wantedColumns.get(OrderProductTransactionTableSettingsColumn.productNumber)?.label || '',
      sortValue: (item: ProductTransaction) => {
        if (item.product.pmdId) {
          return (
            productMasterData.get(item.product.pmdId ?? '')?.productNumber ||
            t(TranslationKey.unknownProduct).singular.label
          );
        }

        if (item.product.containerId) {
          const container = containers.get(item.product.containerId);
          if (!container) return t(TranslationKey.unknownContainer).singular.label;

          const containerType = containerTypes.get(container.containerTypeId);
          if (!containerType) return t(TranslationKey.unknownContainerType).singular.label;

          return containerType.name;
        }

        return '';
      },
      text: (item: ProductTransaction) => {
        if (item.product.pmdId) {
          return (
            productMasterData.get(item.product.pmdId ?? '')?.productNumber ||
            t(TranslationKey.unknownProduct).singular.label
          );
        }

        if (item.product.containerId) {
          const container = containers.get(item.product.containerId);
          if (!container) return t(TranslationKey.unknownContainer).singular.label;

          const containerType = containerTypes.get(container.containerTypeId);
          if (!containerType) return t(TranslationKey.unknownContainerType).singular.label;

          return containerType.name;
        }

        return '';
      },
    });
  }

  if (wantedColumns.has(OrderProductTransactionTableSettingsColumn.lotNumber)) {
    columns.push({
      key: OrderProductTransactionTableSettingsColumn.lotNumber,
      column: OrderProductTransactionTableSettingsColumn.lotNumber,
      name: wantedColumns.get(OrderProductTransactionTableSettingsColumn.lotNumber)?.label || '',
      text: (item: ProductTransaction) => {
        if (!item.product.lotId) return '';
        return lots.get(item.product.lotId)?.number || t(TranslationKey.unknownLot).singular.label;
      },
    });
  }

  if (wantedColumns.has(OrderProductTransactionTableSettingsColumn.lpn)) {
    columns.push({
      key: OrderProductTransactionTableSettingsColumn.lpn,
      column: OrderProductTransactionTableSettingsColumn.lpn,
      name: wantedColumns.get(OrderProductTransactionTableSettingsColumn.lpn)?.label || '',
      text: (item: ProductTransaction) => {
        return item.product.lpn ?? '';
      },
    });
  }

  if (wantedColumns.has(OrderProductTransactionTableSettingsColumn.serialNumber)) {
    columns.push({
      key: OrderProductTransactionTableSettingsColumn.serialNumber,
      column: OrderProductTransactionTableSettingsColumn.serialNumber,
      name: wantedColumns.get(OrderProductTransactionTableSettingsColumn.serialNumber)?.label || '',
      text: (item: ProductTransaction) => {
        return item.product.serialNbr ?? '';
      },
    });
  }

  if (wantedColumns.has(OrderProductTransactionTableSettingsColumn.countryOfOrigin)) {
    columns.push({
      key: OrderProductTransactionTableSettingsColumn.countryOfOrigin,
      column: OrderProductTransactionTableSettingsColumn.countryOfOrigin,
      name: wantedColumns.get(OrderProductTransactionTableSettingsColumn.countryOfOrigin)?.label || '',
      text: (item: ProductTransaction) => {
        return item.product.countryOfOrigin ?? '';
      },
    });
  }

  if (wantedColumns.has(OrderProductTransactionTableSettingsColumn.processedAt)) {
    columns.push({
      key: OrderProductTransactionTableSettingsColumn.processedAt,
      column: OrderProductTransactionTableSettingsColumn.processedAt,
      name: wantedColumns.get(OrderProductTransactionTableSettingsColumn.processedAt)?.label || '',
      sortValue: (item: ProductTransaction) => item.processedAt || '',
      text: (item: ProductTransaction) => {
        if (!item.metadata?.processHistory.length) return '';

        const first = item.metadata.processHistory[item.metadata?.processHistory.length - 1];
        if (first.statusId !== item.statusId) return;
        return DateTimeUtil.toLocaleDateTimeString(locale, new Date(first.processedAt));
      },
    });
  }

  if (wantedColumns.has(OrderProductTransactionTableSettingsColumn.processedBy)) {
    columns.push({
      key: OrderProductTransactionTableSettingsColumn.processedBy,
      column: OrderProductTransactionTableSettingsColumn.processedBy,
      name: wantedColumns.get(OrderProductTransactionTableSettingsColumn.processedBy)?.label || '',
      text: (item: ProductTransaction) => {
        if (!item.metadata?.processHistory.length) return '';

        const first = item.metadata.processHistory[item.metadata?.processHistory.length - 1];
        if (first.statusId !== item.statusId) return;
        return first.user.email;
      },
    });
  }

  if (wantedColumns.has(OrderProductTransactionTableSettingsColumn.quantity)) {
    columns.push({
      weight: 0.5,
      key: OrderProductTransactionTableSettingsColumn.quantity,
      column: OrderProductTransactionTableSettingsColumn.quantity,
      name: wantedColumns.get(OrderProductTransactionTableSettingsColumn.quantity)?.label || '',
      text: (item: ProductTransaction) => {
        const pmd = productMasterData.get(item.product.pmdId ?? '');
        return UnitOfMeasure.quantityWithSuffix(pmd?.unitOfMeasure, item.product.quantity.toString());
      },
    });
  }

  if (wantedColumns.has(OrderProductTransactionTableSettingsColumn.processQuantity)) {
    columns.push({
      key: OrderProductTransactionTableSettingsColumn.processQuantity,
      column: OrderProductTransactionTableSettingsColumn.processQuantity,
      name: wantedColumns.get(OrderProductTransactionTableSettingsColumn.processQuantity)?.label || '',
      text: (item: ProductTransaction) => {
        if (!item.product.processedQuantity) return '';
        const pmd = productMasterData.get(item.product.pmdId ?? '');
        return UnitOfMeasure.quantityWithSuffix(pmd?.unitOfMeasure, item.product.processedQuantity.toString());
      },
    });
  }

  if (wantedColumns.has(OrderProductTransactionTableSettingsColumn.status)) {
    columns.push({
      key: OrderProductTransactionTableSettingsColumn.status,
      column: OrderProductTransactionTableSettingsColumn.status,
      name: wantedColumns.get(OrderProductTransactionTableSettingsColumn.status)?.label || '',
      sortValue: (item: ProductTransaction) => item.statusId,
      text: (item: ProductTransaction) => <ProductTransactionStatusLabel statusId={item.statusId} />,
    });
  }

  const defaultKeys = new Set(Object.keys(OrderProductTransactionTableSettingsColumn));
  const customFieldColumns = [...wantedColumns.values()].filter(item => !defaultKeys.has(item.column));

  customFieldColumns.forEach(item => {
    columns.push({
      key: item.column,
      name: item.label,
      column: item.column,
      sortValue: (pt: ProductTransaction) => {
        const customFieldValue = pt.customFieldValues().find(i => i.name === item.column);
        if (!customFieldValue) return '';
        if (customFieldValue.type === CustomFieldType.date) return dayjs(customFieldValue.value);
        return CustomFieldUtil.getTextForCustomField(customFieldValue, locale);
      },
      text: (pt: ProductTransaction) => {
        const customFieldValue = pt.customFieldValues().find(cf => cf.name === item.column);
        if (!customFieldValue) return '';

        if (customFieldValue.type === CustomFieldType.file) {
          if (!customFieldValue.value) return '';

          const nr = customFieldValue.value.split(';;').length;

          return (
            <Grid container display={'flex'}>
              <Grid item alignContent={'center'}>
                <p>{nr.toString()}</p>
              </Grid>
              <Grid item alignContent={'center'}>
                <AttachFileIcon sx={{ width: 16, height: 16, mb: 0.2 }} />
              </Grid>
            </Grid>
          );
        }

        return CustomFieldUtil.getTextForCustomField(customFieldValue, locale);
      },
    });
  });

  return columns.sort(
    (a: TableHeader<ProductTransaction>, b: TableHeader<ProductTransaction>) =>
      (wantedColumns.get(a.column || '')?.index || -1) - (wantedColumns.get(b.column || '')?.index || -1),
  );
};

export const productTransactionTableFilter = (
  item: ProductTransaction,
  filter: string,
  stockLocations: Map<string, StockLocation>,
  bins: Map<string, Bin>,
  productMasterData: Map<string, ProductMasterData>,
  lots: Map<string, Lot>,
  users: Map<string, User>,
  settings: TableSettings<any>,
) => {
  const filterString = removeDiacritics(toFilterString(filter));
  const wantedColumns = toMap(settings.columns, 'column');

  if (!filter) return true;

  const pmd = productMasterData.get(item.product.pmdId ?? '');

  if (pmd) {
    if (
      wantedColumns.has(OrderProductTransactionTableSettingsColumn.productName) &&
      removeDiacritics(toFilterString(pmd.productName)).includes(filterString)
    ) {
      return true;
    }

    if (
      wantedColumns.has(OrderProductTransactionTableSettingsColumn.productNumber) &&
      removeDiacritics(toFilterString(pmd.productNumber)).includes(filterString)
    ) {
      return true;
    }
  }

  if (
    wantedColumns.has(OrderProductTransactionTableSettingsColumn.status) &&
    toFilterString(item.statusId).includes(filterString)
  ) {
    return true;
  }

  if (
    wantedColumns.has(OrderProductTransactionTableSettingsColumn.type) &&
    toFilterString(item.type).includes(filterString)
  ) {
    return true;
  }

  if (
    wantedColumns.has(OrderProductTransactionTableSettingsColumn.createdAt) &&
    new Date(item.createdAt).toLocaleDateString().toLocaleLowerCase().includes(filterString)
  ) {
    return true;
  }

  if (
    wantedColumns.has(OrderProductTransactionTableSettingsColumn.createdBy) &&
    (removeDiacritics(toFilterString(users.get(item.createdBy)?.email)) ||
      removeDiacritics(
        toFilterString(`${users.get(item.createdBy)?.firstName} ${users.get(item.createdBy)?.lastName}`),
      ))
  )
    if (
      wantedColumns.has(OrderProductTransactionTableSettingsColumn.updatedAt) &&
      new Date(item.updatedAt).toLocaleDateString().toLocaleLowerCase().includes(filterString)
    ) {
      return true;
    }

  if (
    wantedColumns.has(OrderProductTransactionTableSettingsColumn.updatedBy) &&
    (removeDiacritics(toFilterString(users.get(item.updatedBy)?.email)) ||
      removeDiacritics(
        toFilterString(`${users.get(item.updatedBy)?.firstName} ${users.get(item.updatedBy)?.lastName}`),
      ))
  ) {
    return true;
  }
  if (
    wantedColumns.has(OrderProductTransactionTableSettingsColumn.processedAt) &&
    item.processedAt &&
    new Date(item.processedAt).toLocaleDateString().toLocaleLowerCase().includes(filterString)
  ) {
    return true;
  }

  if (
    wantedColumns.has(OrderProductTransactionTableSettingsColumn.processedBy) &&
    item.processedBy &&
    (removeDiacritics(toFilterString(users.get(item.processedBy)?.email)) ||
      removeDiacritics(
        toFilterString(`${users.get(item.processedBy)?.firstName} ${users.get(item.processedBy)?.lastName}`),
      ))
  ) {
    return true;
  }

  if (
    wantedColumns.has(OrderProductTransactionTableSettingsColumn.destinationStockLocation) &&
    item.type === ProductTransactionType.inbound &&
    item.product.toStockLocationId &&
    removeDiacritics(toFilterString(stockLocations.get(item.product.toStockLocationId)?.name)).includes(filterString)
  ) {
    return true;
  }

  if (
    wantedColumns.has(OrderProductTransactionTableSettingsColumn.sourceStockLocation) &&
    item.type === ProductTransactionType.outbound &&
    item.product.fromStockLocationId &&
    removeDiacritics(toFilterString(stockLocations.get(item.product.fromStockLocationId)?.name)).includes(filterString)
  ) {
    return true;
  }

  if (
    wantedColumns.has(OrderProductTransactionTableSettingsColumn.sourceBin) &&
    item.type === ProductTransactionType.outbound &&
    item.product.fromBinId &&
    removeDiacritics(toFilterString(bins.get(item.product.fromBinId)?.name)).includes(filterString)
  ) {
    return true;
  }

  if (
    wantedColumns.has(OrderProductTransactionTableSettingsColumn.destinationBin) &&
    item.type === ProductTransactionType.inbound &&
    item.product.toBinId &&
    removeDiacritics(toFilterString(bins.get(item.product.toBinId)?.name)).includes(filterString)
  ) {
    return true;
  }

  if (
    wantedColumns.has(OrderProductTransactionTableSettingsColumn.lotNumber) &&
    item.product.lotId &&
    removeDiacritics(toFilterString(lots.get(item.product.lotId)?.number))
  ) {
    return true;
  }

  if (
    wantedColumns.has(OrderProductTransactionTableSettingsColumn.serialNumber) &&
    item.product.serialNbr &&
    removeDiacritics(toFilterString(item.product.serialNbr)).includes(filterString)
  ) {
    return true;
  }

  if (
    wantedColumns.has(OrderProductTransactionTableSettingsColumn.lpn) &&
    item.product.lpn &&
    removeDiacritics(toFilterString(item.product.lpn)).includes(filterString)
  ) {
    return true;
  }

  if (
    wantedColumns.has(OrderProductTransactionTableSettingsColumn.countryOfOrigin) &&
    item.product.countryOfOrigin &&
    removeDiacritics(toFilterString(item.product.countryOfOrigin)).includes(filterString)
  ) {
    return true;
  }

  const defaultKeys = new Set(Object.keys(OrderProductTransactionTableSettingsColumn));
  const customFieldColumns = [...wantedColumns.values()].filter(item => !defaultKeys.has(item.column));

  for (const custom of customFieldColumns) {
    const field = item
      .customFieldValues()
      .find(i => i.name === custom.column)
      ?.value.toLocaleLowerCase();
    if (field?.includes(filterString)) return true;
  }

  return false;
};
